import { useEffect, useMemo, useState } from "react";
import {
  Timestamp,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
  type QueryConstraint,
} from "firebase/firestore";
import {
  COL,
  estadoInvitaciones,
  resumirSaldo,
  type ClaseCalendario,
  type CompraPaquete,
  type Cupon,
  type LayoutSala,
  type Paquete,
  type Reservacion,
  type SolicitudBdayPrevia,
  type Usuario,
  anioLocal,
} from "@zociety/core";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";

const ms = (v: unknown): number =>
  v instanceof Timestamp ? v.toMillis() : typeof v === "number" ? v : 0;

interface Estado<T> {
  datos: T;
  cargando: boolean;
  error: string | null;
}

/** Suscripción a una colección con mapeo tipado. */
function useColeccion<T>(
  ruta: string,
  restricciones: QueryConstraint[],
  mapear: (id: string, d: Record<string, unknown>) => T,
  activo = true
): Estado<T[]> {
  const [estado, setEstado] = useState<Estado<T[]>>({ datos: [], cargando: activo, error: null });
  // Las restricciones se reconstruyen en cada render; sin esto la suscripción
  // se cancelaría y volvería a abrirse en bucle.
  const llave = JSON.stringify(restricciones.map((r) => r.type));

  useEffect(() => {
    if (!activo) {
      setEstado({ datos: [], cargando: false, error: null });
      return;
    }
    const cancelar = onSnapshot(
      query(collection(db, ruta), ...restricciones),
      (snap) => {
        setEstado({
          datos: snap.docs.map((d) => mapear(d.id, d.data() as Record<string, unknown>)),
          cargando: false,
          error: null,
        });
      },
      (e) => setEstado({ datos: [], cargando: false, error: e.message })
    );
    return cancelar;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruta, llave, activo]);

  return estado;
}

export function usePaquetes(incluirOcultos = false) {
  const estado = useColeccion<Paquete>(
    COL.PAQUETES,
    [],
    (id, d) => ({
      id,
      nombre: String(d.nombre ?? ""),
      descripcion: d.descripcion ? String(d.descripcion) : undefined,
      precio_centavos: Number(d.precio_centavos ?? 0),
      total_clases: Number(d.total_clases ?? 0),
      vigencia_dias: Number(d.vigencia_dias ?? 30),
      tipo_credito: (d.tipo_credito ?? "clase") as Paquete["tipo_credito"],
      solo_primera_vez: d.solo_primera_vez === true,
      visible_en_tienda: d.visible_en_tienda !== false,
      activo: d.activo !== false,
      orden: Number(d.orden ?? 99),
      sala: d.sala as Paquete["sala"],
      spots: d.spots ? Number(d.spots) : undefined,
      brunch: d.brunch === true ? true : undefined,
    })
  );

  const datos = useMemo(
    () =>
      estado.datos
        .filter((p) => p.activo && (incluirOcultos || p.visible_en_tienda))
        .sort((a, b) => a.orden - b.orden || a.precio_centavos - b.precio_centavos),
    [estado.datos, incluirOcultos]
  );

  return { ...estado, datos };
}

export function useClases(desdeMs: number, hastaMs: number) {
  const estado = useColeccion<ClaseCalendario>(
    COL.CLASES,
    [
      where("fecha_hora", ">=", Timestamp.fromMillis(desdeMs)),
      where("fecha_hora", "<=", Timestamp.fromMillis(hastaMs)),
      orderBy("fecha_hora", "asc"),
    ],
    (id, d) => {
      const ocupados = Array.isArray(d.lugares_ocupados) ? (d.lugares_ocupados as number[]) : [];
      return {
        clase_id: id,
        disciplina: (d.disciplina ?? "ZYCLING") as ClaseCalendario["disciplina"],
        modalidad: String(d.modalidad ?? ""),
        coach_id: String(d.coach_id ?? ""),
        coach_nombre: String(d.coach_nombre ?? "Coach"),
        coach2_id: d.coach2_id ? String(d.coach2_id) : undefined,
        coach2_nombre: d.coach2_nombre ? String(d.coach2_nombre) : undefined,
        fecha_hora_ms: ms(d.fecha_hora),
        duracion_minutos: Number(d.duracion_minutos ?? 50),
        layout_id: String(d.layout_id ?? "zycling_principal"),
        capacidad_total: Number(d.capacidad_total ?? 0),
        lugares_ocupados: ocupados,
        cupo_ocupado: Number(d.cupo_ocupado ?? ocupados.length),
        estado: (d.estado ?? "activa") as ClaseCalendario["estado"],
        clase_privada: d.clase_privada === true,
      };
    }
  );

  const datos = useMemo(
    () => estado.datos.filter((c) => c.estado === "activa" && !c.clase_privada),
    [estado.datos]
  );
  return { ...estado, datos };
}

export function useLayouts() {
  return useColeccion<LayoutSala>(COL.LAYOUTS, [], (id, d) => ({
    id,
    nombre: String(d.nombre ?? ""),
    disciplina: (d.disciplina ?? "ZYCLING") as LayoutSala["disciplina"],
    filas: Number(d.filas ?? 1),
    columnas: Number(d.columnas ?? 1),
    lugares: Array.isArray(d.lugares) ? (d.lugares as LayoutSala["lugares"]) : [],
    dedicado_a: d.dedicado_a ? String(d.dedicado_a) : null,
  }));
}

export function useMisCompras() {
  const { cuenta } = useAuth();
  return useColeccion<CompraPaquete>(
    COL.COMPRAS,
    cuenta ? [where("usuario_id", "==", cuenta.uid)] : [],
    (id, d) => ({
      compra_id: id,
      usuario_id: String(d.usuario_id ?? ""),
      paquete_id: String(d.paquete_id ?? ""),
      nombre_paquete: String(d.nombre_paquete ?? ""),
      precio_pagado_centavos: Number(d.precio_pagado_centavos ?? 0),
      clases_totales: Number(d.clases_totales ?? 0),
      clases_restantes: Number(d.clases_restantes ?? 0),
      fecha_compra_ms: ms(d.fecha_compra),
      fecha_expiracion_ms: ms(d.fecha_expiracion),
      estatus: (d.estatus ?? "activo") as CompraPaquete["estatus"],
      tipo_credito: (d.tipo_credito ?? "clase") as CompraPaquete["tipo_credito"],
      invitaciones_por_ventana: (d.invitaciones_por_ventana ?? {}) as Record<string, number>,
      invitaciones_por_periodo: Number(d.invitaciones_por_periodo ?? 0),
    }),
    Boolean(cuenta)
  );
}

export function useMisReservas() {
  const { cuenta } = useAuth();
  return useColeccion<Reservacion>(
    COL.RESERVACIONES,
    cuenta ? [where("usuario_id", "==", cuenta.uid)] : [],
    (id, d) => ({
      reservacion_id: id,
      usuario_id: String(d.usuario_id ?? ""),
      usuario_nombre: String(d.usuario_nombre ?? ""),
      usuario_email: String(d.usuario_email ?? ""),
      clase_id: String(d.clase_id ?? ""),
      clase_disciplina: (d.clase_disciplina ?? "ZYCLING") as Reservacion["clase_disciplina"],
      clase_modalidad: String(d.clase_modalidad ?? ""),
      clase_coach: String(d.clase_coach ?? ""),
      clase_fecha_hora_ms: ms(d.clase_fecha_hora),
      numero_lugar: Number(d.numero_lugar ?? 0),
      estatus: (d.estatus ?? "reservado") as Reservacion["estatus"],
      asistencia: (d.asistencia ?? "pendiente") as Reservacion["asistencia"],
      fecha_creacion_ms: ms(d.fecha_creacion),
      paquete_compra_id: d.paquete_compra_id ? String(d.paquete_compra_id) : null,
      es_ilimitado: d.es_ilimitado === true,
      es_invitado: d.es_invitado === true ? true : undefined,
      reservacion_invitado_id: d.reservacion_invitado_id ? String(d.reservacion_invitado_id) : undefined,
      invitacion_ventana: typeof d.invitacion_ventana === "number" ? d.invitacion_ventana : undefined,
    }),
    Boolean(cuenta)
  );
}

/** Saldo consolidado del alumno, calculado con las reglas del core. */
export function useSaldo() {
  const { datos: compras, cargando } = useMisCompras();
  const ahora = Date.now();

  return useMemo(() => {
    const resumen = resumirSaldo(compras, ahora);
    const pases = compras
      .map((c) => estadoInvitaciones(c, ahora))
      .filter((e) => e.disponibles > 0);

    return {
      cargando,
      ...resumen,
      tieneAcceso: resumen.creditos_disponibles > 0 || resumen.unlimited_activos.length > 0,
      pasesInvitado: pases.reduce((n, p) => n + p.disponibles, 0),
      paseVenceMs: pases[0]?.vence_ms ?? null,
      compras,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compras, cargando]);
}

export function useNotificaciones() {
  const { cuenta } = useAuth();
  const [datos, setDatos] = useState<
    { id: string; titulo: string; mensaje: string; link?: string; leida: boolean; fecha_ms: number }[]
  >([]);

  useEffect(() => {
    if (!cuenta) {
      setDatos([]);
      return;
    }
    return onSnapshot(
      query(
        collection(doc(db, COL.USUARIOS, cuenta.uid), COL.NOTIFICACIONES),
        orderBy("fecha", "desc")
      ),
      (snap) =>
        setDatos(
          snap.docs.map((d) => ({
            id: d.id,
            titulo: String(d.data().titulo ?? ""),
            mensaje: String(d.data().mensaje ?? ""),
            link: d.data().link ?? undefined,
            leida: d.data().leida === true,
            fecha_ms: ms(d.data().fecha),
          }))
        ),
      () => setDatos([])
    );
  }, [cuenta]);

  return { datos, sinLeer: datos.filter((n) => !n.leida).length };
}

export function useCupones() {
  return useColeccion<Cupon>(COL.CUPONES, [], (id, d) => ({
    cupon_id: id,
    codigo: String(d.codigo ?? "").toUpperCase(),
    tipo_descuento: (d.tipo_descuento ?? "porcentaje") as Cupon["tipo_descuento"],
    valor: Number(d.valor ?? 0),
    paquetes_aplicables: Array.isArray(d.paquetes_aplicables) ? (d.paquetes_aplicables as string[]) : [],
    fecha_inicio_ms: ms(d.fecha_inicio),
    fecha_fin_ms: ms(d.fecha_fin),
    limite_global: Number(d.limite_global ?? 0),
    limite_por_usuario: Number(d.limite_por_usuario ?? 1),
    usos_actuales: Number(d.usos_actuales ?? 0),
    estado: (d.estado ?? "activo") as Cupon["estado"],
    restricciones: d.restricciones as Cupon["restricciones"],
  }));
}

// ============================================================
// Portal de administración
//
// Los hooks de arriba sirven pantallas cuyo filtro nunca cambia durante la
// vida del componente, así que a `useColeccion` le basta con identificar la
// consulta por el TIPO de sus restricciones. El panel no cumple esa premisa:
// la agenda salta de semana, la asistencia salta de clase y el CRM salta de
// alumno sin desmontar nada. Por eso estos hooks usan `useColeccionCon`, que
// recibe una llave construida con los VALORES del filtro y sí vuelve a
// suscribirse cuando esos valores cambian.
// ============================================================

/** Igual que `useColeccion`, pero la suscripción se rehace cuando cambia `clave`. */
function useColeccionCon<T>(
  ruta: string,
  restricciones: QueryConstraint[],
  mapear: (id: string, d: Record<string, unknown>) => T,
  clave: string,
  activo = true
): Estado<T[]> {
  const [estado, setEstado] = useState<Estado<T[]>>({ datos: [], cargando: activo, error: null });

  useEffect(() => {
    if (!activo) {
      setEstado({ datos: [], cargando: false, error: null });
      return;
    }
    setEstado((e) => ({ ...e, cargando: true }));
    const cancelar = onSnapshot(
      query(collection(db, ruta), ...restricciones),
      (snap) => {
        setEstado({
          datos: snap.docs.map((d) => mapear(d.id, d.data() as Record<string, unknown>)),
          cargando: false,
          error: null,
        });
      },
      (e) => setEstado({ datos: [], cargando: false, error: e.message })
    );
    return cancelar;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruta, clave, activo]);

  return estado;
}

/** Suscripción a un documento suelto. Usada por las pantallas de configuración. */
export function useDocumento<T>(
  ruta: string,
  id: string,
  mapear: (d: Record<string, unknown>) => T
): Estado<T | null> {
  const [estado, setEstado] = useState<Estado<T | null>>({ datos: null, cargando: true, error: null });

  useEffect(() => {
    setEstado({ datos: null, cargando: true, error: null });
    return onSnapshot(
      doc(db, ruta, id),
      (snap) => {
        setEstado({
          datos: snap.exists() ? mapear(snap.data() as Record<string, unknown>) : null,
          cargando: false,
          error: null,
        });
      },
      (e) => setEstado({ datos: null, cargando: false, error: e.message })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruta, id]);

  return estado;
}

/** Perfil visto por el panel: incluye los campos de vitrina que trae el staff. */
export type UsuarioAdmin = Usuario & {
  especialidad?: string;
  biografia?: string;
  imagen?: string;
  fecha_ingreso?: string | null;
};

function aUsuarioAdmin(id: string, d: Record<string, unknown>): UsuarioAdmin {
  return {
    uid: id,
    email: String(d.email ?? ""),
    nombre: String(d.nombre ?? ""),
    apellido: String(d.apellido ?? ""),
    telefono: String(d.telefono ?? ""),
    fecha_nacimiento: typeof d.fecha_nacimiento === "string" ? d.fecha_nacimiento : null,
    genero: d.genero as Usuario["genero"],
    rol: (d.rol ?? "alumno") as Usuario["rol"],
    estado: (d.estado ?? "activo") as Usuario["estado"],
    fecha_registro_ms: ms(d.fecha_registro),
    clases_disponibles: Number(d.clases_disponibles ?? 0),
    faltas_acumuladas: Number(d.faltas_acumuladas ?? 0),
    stats_clases_asistidas: Number(d.stats_clases_asistidas ?? 0),
    stats_clases_programadas: Number(d.stats_clases_programadas ?? 0),
    stats_paquetes_comprados: Number(d.stats_paquetes_comprados ?? 0),
    lesion_activa: d.lesion_activa === true,
    lesion_descripcion: String(d.lesion_descripcion ?? ""),
    lesion_actualizada_ms: ms(d.lesion_actualizada) || null,
    especialidad: d.especialidad ? String(d.especialidad) : undefined,
    biografia: d.biografia ? String(d.biografia) : undefined,
    imagen: d.imagen ? String(d.imagen) : undefined,
    fecha_ingreso: typeof d.fecha_ingreso === "string" ? d.fecha_ingreso : null,
    disponibilidad: (d.disponibilidad ?? undefined) as Usuario["disponibilidad"],
  };
}

/** Directorio completo. Solo el staff puede leerlo (ver firestore.rules). */
export function useUsuarios() {
  return useColeccion<UsuarioAdmin>(COL.USUARIOS, [], aUsuarioAdmin);
}

/**
 * `Paquete` del core no declara `invitaciones_por_periodo`, aunque
 * `paqueteSchema` sí lo valida y las compras lo copian. El panel necesita
 * editarlo, así que se extiende aquí en lugar de tocar el paquete compartido.
 */
export type PaqueteAdmin = Paquete & { invitaciones_por_periodo: number };

/** Catálogo completo: incluye los inactivos y los ocultos en tienda. */
export function usePaquetesAdmin() {
  const estado = useColeccion<PaqueteAdmin>(COL.PAQUETES, [], (id, d) => ({
    id,
    nombre: String(d.nombre ?? ""),
    descripcion: d.descripcion ? String(d.descripcion) : undefined,
    precio_centavos: Number(d.precio_centavos ?? 0),
    total_clases: Number(d.total_clases ?? 0),
    vigencia_dias: Number(d.vigencia_dias ?? 30),
    tipo_credito: (d.tipo_credito ?? "clase") as Paquete["tipo_credito"],
    solo_primera_vez: d.solo_primera_vez === true,
    visible_en_tienda: d.visible_en_tienda !== false,
    activo: d.activo !== false,
    orden: Number(d.orden ?? 99),
    invitaciones_por_periodo: Number(d.invitaciones_por_periodo ?? 0),
    sala: d.sala as Paquete["sala"],
    spots: d.spots ? Number(d.spots) : undefined,
    brunch: d.brunch === true ? true : undefined,
  }));

  const datos = useMemo(
    () => [...estado.datos].sort((a, b) => a.orden - b.orden || a.precio_centavos - b.precio_centavos),
    [estado.datos]
  );
  return { ...estado, datos };
}

function aReservacionAdmin(id: string, d: Record<string, unknown>): Reservacion {
  return {
    reservacion_id: id,
    usuario_id: String(d.usuario_id ?? ""),
    usuario_nombre: String(d.usuario_nombre ?? ""),
    usuario_email: String(d.usuario_email ?? ""),
    clase_id: String(d.clase_id ?? ""),
    clase_disciplina: (d.clase_disciplina ?? "ZYCLING") as Reservacion["clase_disciplina"],
    clase_modalidad: String(d.clase_modalidad ?? ""),
    clase_coach: String(d.clase_coach ?? ""),
    clase_fecha_hora_ms: ms(d.clase_fecha_hora),
    numero_lugar: Number(d.numero_lugar ?? 0),
    estatus: (d.estatus ?? "reservado") as Reservacion["estatus"],
    asistencia: (d.asistencia ?? "pendiente") as Reservacion["asistencia"],
    fecha_creacion_ms: ms(d.fecha_creacion),
    paquete_compra_id: d.paquete_compra_id ? String(d.paquete_compra_id) : null,
    es_ilimitado: d.es_ilimitado === true,
    es_invitado: d.es_invitado === true ? true : undefined,
    titular_id: d.titular_id ? String(d.titular_id) : undefined,
    invitacion_ventana: typeof d.invitacion_ventana === "number" ? d.invitacion_ventana : undefined,
  };
}

/** Lista de inscritos de una clase, para el pase de lista. */
export function useReservasDeClase(claseId: string | null) {
  return useColeccionCon<Reservacion>(
    COL.RESERVACIONES,
    claseId ? [where("clase_id", "==", claseId)] : [],
    aReservacionAdmin,
    `clase:${claseId ?? ""}`,
    Boolean(claseId)
  );
}

/** Historial de reservas de un alumno concreto. */
export function useReservasDeUsuario(usuarioId: string | null) {
  return useColeccionCon<Reservacion>(
    COL.RESERVACIONES,
    usuarioId ? [where("usuario_id", "==", usuarioId)] : [],
    aReservacionAdmin,
    `usuario:${usuarioId ?? ""}`,
    Boolean(usuarioId)
  );
}

/** Reservas de un periodo. Alimenta las métricas de ocupación y asistencia. */
export function useReservasEnRango(desdeMs: number, hastaMs: number, activo = true) {
  return useColeccionCon<Reservacion>(
    COL.RESERVACIONES,
    [
      where("clase_fecha_hora", ">=", Timestamp.fromMillis(desdeMs)),
      where("clase_fecha_hora", "<=", Timestamp.fromMillis(hastaMs)),
    ],
    aReservacionAdmin,
    `rango:${desdeMs}:${hastaMs}`,
    activo
  );
}

function aCompraAdmin(id: string, d: Record<string, unknown>): CompraPaquete {
  return {
    compra_id: id,
    usuario_id: String(d.usuario_id ?? ""),
    paquete_id: String(d.paquete_id ?? ""),
    nombre_paquete: String(d.nombre_paquete ?? ""),
    precio_pagado_centavos: Number(d.precio_pagado_centavos ?? 0),
    clases_totales: Number(d.clases_totales ?? 0),
    clases_restantes: Number(d.clases_restantes ?? 0),
    fecha_compra_ms: ms(d.fecha_compra),
    fecha_expiracion_ms: ms(d.fecha_expiracion),
    estatus: (d.estatus ?? "activo") as CompraPaquete["estatus"],
    tipo_credito: (d.tipo_credito ?? "clase") as CompraPaquete["tipo_credito"],
    invitaciones_por_ventana: (d.invitaciones_por_ventana ?? {}) as Record<string, number>,
    invitaciones_por_periodo: Number(d.invitaciones_por_periodo ?? 0),
  };
}

export function useComprasDeUsuario(usuarioId: string | null) {
  return useColeccionCon<CompraPaquete>(
    COL.COMPRAS,
    usuarioId ? [where("usuario_id", "==", usuarioId)] : [],
    aCompraAdmin,
    `compras:${usuarioId ?? ""}`,
    Boolean(usuarioId)
  );
}

/** Compras de un periodo. Base de los ingresos y de los paquetes vendidos. */
export function useComprasEnRango(desdeMs: number, hastaMs: number, activo = true) {
  return useColeccionCon<CompraPaquete>(
    COL.COMPRAS,
    [
      where("fecha_compra", ">=", Timestamp.fromMillis(desdeMs)),
      where("fecha_compra", "<=", Timestamp.fromMillis(hastaMs)),
    ],
    aCompraAdmin,
    `comprasRango:${desdeMs}:${hastaMs}`,
    activo
  );
}

/** Toda la agenda del periodo, incluidas canceladas y privadas. */
export function useClasesAdmin(desdeMs: number, hastaMs: number) {
  return useColeccionCon<ClaseCalendario>(
    COL.CLASES,
    [
      where("fecha_hora", ">=", Timestamp.fromMillis(desdeMs)),
      where("fecha_hora", "<=", Timestamp.fromMillis(hastaMs)),
      orderBy("fecha_hora", "asc"),
    ],
    (id, d) => {
      const ocupados = Array.isArray(d.lugares_ocupados) ? (d.lugares_ocupados as number[]) : [];
      return {
        clase_id: id,
        disciplina: (d.disciplina ?? "ZYCLING") as ClaseCalendario["disciplina"],
        modalidad: String(d.modalidad ?? ""),
        coach_id: String(d.coach_id ?? ""),
        coach_nombre: String(d.coach_nombre ?? "Coach"),
        coach2_id: d.coach2_id ? String(d.coach2_id) : undefined,
        coach2_nombre: d.coach2_nombre ? String(d.coach2_nombre) : undefined,
        fecha_hora_ms: ms(d.fecha_hora),
        duracion_minutos: Number(d.duracion_minutos ?? 50),
        layout_id: String(d.layout_id ?? "zycling_principal"),
        capacidad_total: Number(d.capacidad_total ?? 0),
        lugares_ocupados: ocupados,
        cupo_ocupado: Number(d.cupo_ocupado ?? ocupados.length),
        estado: (d.estado ?? "activa") as ClaseCalendario["estado"],
        clase_privada: d.clase_privada === true,
      };
    },
    `agenda:${desdeMs}:${hastaMs}`
  );
}

export interface Multa {
  multa_id: string;
  usuario_id: string;
  usuario_nombre: string;
  usuario_email: string;
  clase_id: string;
  reservacion_id: string;
  motivo: string;
  monto_centavos: number;
  estado: "pendiente" | "pagada";
  fecha_creacion_ms: number;
}

function aMulta(id: string, d: Record<string, unknown>): Multa {
  return {
    multa_id: id,
    usuario_id: String(d.usuario_id ?? ""),
    usuario_nombre: String(d.usuario_nombre ?? ""),
    usuario_email: String(d.usuario_email ?? ""),
    clase_id: String(d.clase_id ?? ""),
    reservacion_id: String(d.reservacion_id ?? ""),
    motivo: String(d.motivo ?? ""),
    monto_centavos: Number(d.monto_centavos ?? 0),
    estado: (d.estado ?? "pendiente") as Multa["estado"],
    fecha_creacion_ms: ms(d.fecha_creacion),
  };
}

export function useMultasDeUsuario(usuarioId: string | null) {
  return useColeccionCon<Multa>(
    COL.MULTAS,
    usuarioId ? [where("usuario_id", "==", usuarioId)] : [],
    aMulta,
    `multas:${usuarioId ?? ""}`,
    Boolean(usuarioId)
  );
}

export interface ArticuloMerch {
  id: string;
  nombre: string;
  descripcion: string;
  precio_centavos: number;
  stock: number;
  activo: boolean;
  imagen: string;
  categoria: string;
}

export function useMerch() {
  return useColeccion<ArticuloMerch>(COL.MERCH, [], (id, d) => ({
    id,
    nombre: String(d.nombre ?? ""),
    descripcion: String(d.descripcion ?? ""),
    precio_centavos: Number(d.precio_centavos ?? 0),
    stock: Number(d.stock ?? 0),
    activo: d.activo !== false,
    imagen: String(d.imagen ?? ""),
    categoria: String(d.categoria ?? ""),
  }));
}

/**
 * Vitrina pública de coaches. Nunca contiene correo, teléfono ni fecha de
 * nacimiento: esos datos viven en `usuarios`, que no es de lectura abierta.
 */
export interface CoachVitrina {
  id: string;
  nombre: string;
  apellido: string;
  rol_staff: string;
  especialidad: string;
  biografia: string;
  imagen: string;
  orden: number;
}

export function useCoachesVitrina() {
  const estado = useColeccion<CoachVitrina>("coaches", [], (id, d) => ({
    id,
    nombre: String(d.nombre ?? ""),
    apellido: String(d.apellido ?? ""),
    rol_staff: String(d.rol_staff ?? "Coach"),
    especialidad: String(d.especialidad ?? ""),
    biografia: String(d.biografia ?? ""),
    imagen: String(d.imagen ?? ""),
    orden: Number(d.orden ?? 99),
  }));

  const datos = useMemo(
    () => [...estado.datos].sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre, "es")),
    [estado.datos]
  );
  return { ...estado, datos };
}

export interface FotoInstalacion {
  id: string;
  titulo: string;
  descripcion: string;
  url: string;
  orden: number;
}

/**
 * Galería del estudio.
 *
 * Las fotos sembradas en la v1 guardan la ruta en `imagen`; el editor del panel
 * escribe `url`. Se aceptan las dos: sin el respaldo, las tres fotos originales
 * se veían rotas en el panel.
 */
export function useInstalaciones() {
  const estado = useColeccion<FotoInstalacion>(COL.INSTALACIONES, [], (id, d) => ({
    id,
    titulo: String(d.titulo ?? ""),
    descripcion: String(d.descripcion ?? ""),
    url: String(d.url ?? d.imagen ?? ""),
    orden: Number(d.orden ?? 99),
  }));

  const datos = useMemo(() => [...estado.datos].sort((a, b) => a.orden - b.orden), [estado.datos]);
  return { ...estado, datos };
}

export const ESTADOS_BDAY = ["pendiente_aprobacion", "aprobada", "rechazada"] as const;
export type EstadoBday = (typeof ESTADOS_BDAY)[number];

export interface SolicitudBday {
  solicitud_id: string;
  usuario_id: string;
  usuario_nombre: string;
  usuario_email: string;
  paquete_nombre: string;
  paquete_sala: string | null;
  spots: number | null;
  brunch: boolean;
  precio_total_centavos: number;
  monto_pagado_centavos: number;
  modalidad_pago: string;
  fecha_deseada: string | null;
  hora_deseada: string | null;
  fecha_confirmada: string | null;
  hora_confirmada: string | null;
  nota_admin: string;
  estado: EstadoBday;
  fecha_solicitud_ms: number;
}

/**
 * Solicitudes de Bday Deal del propio alumno, en la forma reducida que usa la
 * regla del cupo anual por sala.
 */
export function useMisSolicitudesBday(): SolicitudBdayPrevia[] {
  const { cuenta } = useAuth();
  const [datos, setDatos] = useState<SolicitudBdayPrevia[]>([]);

  useEffect(() => {
    if (!cuenta) {
      setDatos([]);
      return;
    }
    return onSnapshot(
      query(collection(db, COL.SOLICITUDES_BDAY), where("usuario_id", "==", cuenta.uid)),
      (snap) =>
        setDatos(
          snap.docs.map((d) => ({
            sala: String(d.data().paquete_sala ?? ""),
            anio: anioLocal(ms(d.data().fecha_solicitud)),
            estado: String(d.data().estado ?? ""),
          }))
        ),
      () => setDatos([])
    );
  }, [cuenta]);

  return datos;
}

export function useSolicitudesBday() {
  const estado = useColeccion<SolicitudBday>(COL.SOLICITUDES_BDAY, [], (id, d) => ({
    solicitud_id: id,
    usuario_id: String(d.usuario_id ?? ""),
    usuario_nombre: String(d.usuario_nombre ?? ""),
    usuario_email: String(d.usuario_email ?? ""),
    paquete_nombre: String(d.paquete_nombre ?? ""),
    paquete_sala: d.paquete_sala ? String(d.paquete_sala) : null,
    spots: typeof d.spots === "number" ? d.spots : null,
    brunch: d.brunch === true,
    precio_total_centavos: Number(d.precio_total_centavos ?? 0),
    monto_pagado_centavos: Number(d.monto_pagado_centavos ?? 0),
    modalidad_pago: String(d.modalidad_pago ?? "100"),
    fecha_deseada: typeof d.fecha_deseada === "string" ? d.fecha_deseada : null,
    hora_deseada: typeof d.hora_deseada === "string" ? d.hora_deseada : null,
    fecha_confirmada: typeof d.fecha_confirmada === "string" ? d.fecha_confirmada : null,
    hora_confirmada: typeof d.hora_confirmada === "string" ? d.hora_confirmada : null,
    nota_admin: String(d.nota_admin ?? ""),
    estado: (d.estado ?? "pendiente_aprobacion") as EstadoBday,
    fecha_solicitud_ms: ms(d.fecha_solicitud),
  }));

  const datos = useMemo(
    () => [...estado.datos].sort((a, b) => b.fecha_solicitud_ms - a.fecha_solicitud_ms),
    [estado.datos]
  );
  return { ...estado, datos };
}
